import * as React from 'react';
import clsxm from 'src/utils/clsxm';
import UnstyledLink, { UnstyledLinkProps } from './UnstyledLink';

enum ButtonVariant {
  'primary',
  'secondary',
  'light',
  'error',
  'success',
  'outlined',
  'text'
}

type ButtonLinkProps = {
  direction?: 'right' | 'left';
  isArrow?: boolean;
  arrowClassName?: string;
  variant?: keyof typeof ButtonVariant;
} & UnstyledLinkProps;

const ButtonLink = React.forwardRef<HTMLAnchorElement, ButtonLinkProps>(
  (
    {
      children,
      className,
      isArrow,
      direction = 'right',
      variant = 'primary',
      arrowClassName,
      ...rest
    },
    ref
  ) => {
    return (
      <UnstyledLink
        ref={ref}
        className={clsxm(
          'relative inline-flex justify-center items items-center px-4 py-3 rounded-full text-base font-normal',
          'focus:outline-none gap-x-6  text-center',
          'shadow-sm',
          'transition ease-in-out delay-150 duration-300',
          [
            variant === 'primary' && [
              'bg-primary text-white font-medium',
              'hover:bg-primary hover:text-white hover:shadow-md hover:shadow-primary/70',
              'active:bg-primary',
              'disabled:bg-primary disabled:bg-opacity-60 disabled:hover:bg-primary disabled:hover:bg-opacity-60 disabled:hover:shadow-none'
            ],
            variant === 'secondary' && [
              'bg-gray-400 text-primary',
              'hover:bg-gray-400 hover:text-primary',
              'active:bg-gray-400',
              'disabled:bg-gray-400 disabled:bg-opacity-30 disabled:hover:bg-gray-400 disabled:hover:bg-opacity-30'
            ],
            variant === 'light' && [
              'bg-white text-black',
              'hover:bg-primary hover:text-white',
              'active:bg-primary',
              'disabled:bg-white disabled:bg-opacity-30 disabled:hover:bg-white disabled:hover:bg-opacity-30'
            ],
            variant === 'success' && [
              'bg-success text-white',
              'hover:bg-success hover:text-white',
              'active:bg-success',
              'disabled:bg-success disabled:bg-opacity-30 disabled:hover:bg-success disabled:hover:bg-opacity-30'
            ],
            variant === 'error' && [
              'bg-error text-white',
              'hover:bg-error hover:text-white',
              'active:bg-error',
              'disabled:bg-error disabled:bg-opacity-30 disabled:hover:bg-error disabled:hover:bg-opacity-30'
            ],
            variant === 'outlined' && [
              'border border-[#D8E0EE] bg-transparent text-primary',
              'hover:bg-[#C1E8FD]',
              'active:bg-[#C1E8FD]',
              'disabled:bg-gray-400 disabled:bg-opacity-30 disabled:hover:bg-gray-400 disabled:hover:bg-opacity-30 shadow-none'
            ],
            variant === 'text' && [
              'font-semibold shadow-none',
              'bg-transparent text-primary',
              'hover:bg-[#C1E8FD]',
              'active:bg-[#C1E8FD]',
              'disabled:bg-gray-400 disabled:bg-opacity-30 disabled:hover:bg-gray-400 disabled:hover:bg-opacity-30'
            ]
          ],
          'disabled:cursor-not-allowed',
          className
        )}
        {...rest}
      >
        {children}
        {isArrow && (
          <svg
            width="54"
            height="24"
            viewBox="0 0 54 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={clsxm(
              'relative',
              'transition-transform duration-200',
              direction === 'right'
                ? 'motion-safe:-translate-x-1'
                : 'rotate-180',
              'group-hover:translate-x-0',
              arrowClassName
            )}
          >
            <path
              d="M44.4299 5.92969L50.4999 11.9997L44.4299 18.0697"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M3.5 12H50.33"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        )}
      </UnstyledLink>
    );
  }
);

export default ButtonLink;
